<template>
  <div class="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-white">

    <!-- Navbar: fixed, transparent top -->
    <header class="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
            :class="scrolled
              ? 'bg-white/90 dark:bg-gray-950/90 backdrop-blur-md border-b border-gray-200/60 dark:border-gray-800/60 shadow-sm'
              : 'bg-transparent'">
      <div class="max-w-6xl mx-auto px-5 sm:px-8">
        <div class="flex items-center justify-between h-16">
          <!-- Logo -->
          <div class="flex items-center gap-2.5">
            <div class="w-8 h-8 rounded-lg bg-[#1DB954] flex items-center justify-center flex-shrink-0">
              <Music class="w-4.5 h-4.5 text-white" style="width:18px;height:18px" />
            </div>
            <span class="font-semibold text-base tracking-tight text-gray-900 dark:text-white">
              CD Shelfify prj.
            </span>
          </div>

          <!-- Nav login button -->
          <a
            href="/api/v1/auth/login"
            class="inline-flex items-center gap-2 bg-[#1DB954] hover:bg-[#18a349] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-150"
          >
            <SpotifyIcon class="w-4 h-4" />
            Login with Spotify
          </a>
        </div>
      </div>
    </header>

    <!-- Hero Section -->
    <section class="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden">
      <!-- Background gradient blobs -->
      <div class="absolute inset-0 -z-10 overflow-hidden" aria-hidden="true">
        <div class="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-gradient-to-b from-[#1DB954]/10 to-transparent rounded-full blur-3xl dark:from-[#1DB954]/6"></div>
        <div class="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-violet-500/8 to-transparent rounded-full blur-3xl dark:from-violet-500/5"></div>
        <div class="absolute top-40 left-0 w-72 h-72 bg-gradient-to-br from-sky-400/8 to-transparent rounded-full blur-3xl dark:from-sky-400/4"></div>
      </div>

      <div class="max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <!-- Eyebrow pill -->
        <div class="inline-flex items-center gap-2 bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 text-xs font-medium px-3.5 py-1.5 rounded-full mb-8">
          <span class="w-1.5 h-1.5 rounded-full bg-[#1DB954] inline-block"></span>
          Spotify Album Manager
        </div>

        <h1 class="text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.08] tracking-tight text-gray-900 dark:text-white mb-6">
          Your Spotify Albums,<br />
          <span class="text-[#1DB954]">Beautifully Organized</span>
        </h1>

        <p class="text-lg sm:text-xl text-gray-500 dark:text-gray-400 leading-relaxed max-w-2xl mx-auto mb-10">
          Create folders, tag albums, browse by genre and artist.
          Take control of your music collection.
        </p>

        <div class="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="/api/v1/auth/login"
            class="inline-flex items-center justify-center gap-2.5 bg-[#1DB954] hover:bg-[#18a349] text-white font-semibold text-base px-7 py-3.5 rounded-full transition-colors duration-150 shadow-lg shadow-[#1DB954]/20"
          >
            <SpotifyIcon class="w-5 h-5" />
            Get Started with Spotify
          </a>
          <a
            href="#features"
            class="inline-flex items-center justify-center gap-2 border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-600 font-medium text-base px-7 py-3.5 rounded-full transition-colors duration-150"
          >
            See features
            <ArrowRight class="w-4 h-4" />
          </a>
        </div>

        <!-- Abstract visual: mock shelf -->
        <div class="mt-20 relative max-w-3xl mx-auto">
          <!-- Outer frame -->
          <div class="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/60 overflow-hidden shadow-2xl shadow-black/8 dark:shadow-black/40">
            <!-- Fake browser bar -->
            <div class="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
              <div class="flex gap-1.5">
                <div class="w-3 h-3 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                <div class="w-3 h-3 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                <div class="w-3 h-3 rounded-full bg-gray-200 dark:bg-gray-700"></div>
              </div>
              <div class="flex-1 mx-4 h-5 rounded-md bg-gray-100 dark:bg-gray-800 text-[11px] text-gray-400 flex items-center px-3">
                cdshelfify.app/albums
              </div>
            </div>
            <!-- Mock album grid -->
            <div class="p-6">
              <!-- Folder tabs -->
              <div class="flex gap-2 mb-5">
                <div class="flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 shadow-sm">
                  <Folder class="w-3.5 h-3.5 text-[#1DB954]" /> Jazz
                </div>
                <div class="flex items-center gap-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 shadow-sm">
                  <Folder class="w-3.5 h-3.5 text-violet-400" /> Electronic
                </div>
                <div class="flex items-center gap-1.5 bg-[#1DB954]/10 border border-[#1DB954]/30 rounded-lg px-3 py-1.5 text-xs font-medium text-[#1DB954]">
                  <Folder class="w-3.5 h-3.5" /> All Albums
                </div>
              </div>
              <!-- Album grid -->
              <div class="grid grid-cols-4 sm:grid-cols-6 gap-3">
                <div v-for="(color, i) in albumColors" :key="i"
                     class="aspect-square rounded-xl flex items-center justify-center text-white/60 text-xs font-bold"
                     :style="{ background: color }">
                </div>
              </div>
              <!-- Tag strip -->
              <div class="flex gap-2 mt-5 flex-wrap">
                <span v-for="(tag, i) in demoTags" :key="i"
                      class="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border"
                      :style="{ backgroundColor: tag.bg, borderColor: tag.border, color: tag.text }">
                  <Tag class="w-3 h-3" />{{ tag.label }}
                </span>
              </div>
            </div>
          </div>
          <!-- Glow behind mock -->
          <div class="absolute inset-x-12 -bottom-6 h-12 bg-[#1DB954]/15 blur-2xl rounded-full -z-10"></div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section id="features" class="py-24 sm:py-32 bg-gray-50 dark:bg-gray-950/50">
      <div class="max-w-6xl mx-auto px-5 sm:px-8">
        <div class="text-center mb-16">
          <p class="text-[#1DB954] font-semibold text-sm uppercase tracking-widest mb-3">Features</p>
          <h2 class="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">
            Everything your collection needs
          </h2>
          <p class="text-gray-500 dark:text-gray-400 text-lg max-w-xl mx-auto">
            Powerful tools for music lovers who want complete control over their Spotify library.
          </p>
        </div>

        <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div
            v-for="feature in features"
            :key="feature.title"
            class="group bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 hover:border-gray-300 dark:hover:border-gray-700 transition-all duration-200 hover:shadow-md hover:shadow-black/4 dark:hover:shadow-black/20"
          >
            <div
              class="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
              :class="feature.iconBg"
            >
              <component :is="feature.icon" class="w-5 h-5" :class="feature.iconColor" />
            </div>
            <h3 class="font-semibold text-gray-900 dark:text-white mb-2">{{ feature.title }}</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works -->
    <section class="py-24 sm:py-32 bg-white dark:bg-gray-950">
      <div class="max-w-6xl mx-auto px-5 sm:px-8">
        <div class="text-center mb-16">
          <p class="text-[#1DB954] font-semibold text-sm uppercase tracking-widest mb-3">How it works</p>
          <h2 class="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
            Up and running in minutes
          </h2>
        </div>

        <div class="grid md:grid-cols-3 gap-8 relative">
          <!-- Connector line (desktop only) -->
          <div class="hidden md:block absolute top-10 left-1/3 right-1/3 h-px bg-gradient-to-r from-gray-200 via-[#1DB954]/40 to-gray-200 dark:from-gray-800 dark:via-[#1DB954]/30 dark:to-gray-800"></div>

          <div v-for="(step, i) in steps" :key="i" class="text-center">
            <div class="relative inline-flex items-center justify-center w-20 h-20 mb-6">
              <div class="absolute inset-0 rounded-full"
                   :class="i === 0 ? 'bg-[#1DB954]/10' : i === 1 ? 'bg-violet-500/10' : 'bg-sky-500/10'"></div>
              <span class="text-2xl font-bold"
                    :class="i === 0 ? 'text-[#1DB954]' : i === 1 ? 'text-violet-500' : 'text-sky-500'">
                {{ i + 1 }}
              </span>
            </div>
            <div class="flex items-center justify-center gap-2 mb-3">
              <component :is="step.icon" class="w-4 h-4 text-gray-400" />
              <h3 class="font-semibold text-gray-900 dark:text-white text-lg">{{ step.title }}</h3>
            </div>
            <p class="text-gray-500 dark:text-gray-400 text-sm leading-relaxed max-w-xs mx-auto">
              {{ step.description }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="py-24 sm:py-32 bg-gray-950 dark:bg-black relative overflow-hidden">
      <div class="absolute inset-0 -z-10" aria-hidden="true">
        <div class="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-64 bg-[#1DB954]/8 rounded-full blur-3xl"></div>
      </div>

      <div class="max-w-2xl mx-auto px-5 sm:px-8 text-center">
        <CheckCircle class="w-10 h-10 text-[#1DB954] mx-auto mb-6" />
        <h2 class="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
          Ready to organize your music?
        </h2>
        <p class="text-gray-400 text-lg mb-10">
          Connect your Spotify account and start curating your perfect collection today.
        </p>
        <a
          href="/api/v1/auth/login"
          class="inline-flex items-center justify-center gap-2.5 bg-[#1DB954] hover:bg-[#18a349] text-white font-semibold text-base px-8 py-4 rounded-full transition-colors duration-150 shadow-xl shadow-[#1DB954]/25"
        >
          <SpotifyIcon class="w-5 h-5" />
          Get Started with Spotify
        </a>
        <p class="mt-5 text-gray-600 text-sm">Free to use &nbsp;&middot;&nbsp; No credit card required</p>
      </div>
    </section>

    <!-- Footer -->
    <footer class="bg-gray-950 dark:bg-black border-t border-gray-900">
      <div class="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div class="flex items-center gap-2">
          <div class="w-6 h-6 rounded-md bg-[#1DB954] flex items-center justify-center flex-shrink-0">
            <Music class="text-white" style="width:13px;height:13px" />
          </div>
          <span class="text-gray-500 text-sm">CD Shelfify prj.</span>
        </div>
        <p class="text-gray-600 text-sm">
          Built with
          <a href="https://vuejs.org" target="_blank" rel="noopener"
             class="text-gray-400 hover:text-white transition-colors">Vue.js</a>
          &amp;
          <a href="https://fastapi.tiangolo.com" target="_blank" rel="noopener"
             class="text-gray-400 hover:text-white transition-colors">FastAPI</a>
        </p>
      </div>
    </footer>

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import {
  Folder, Tag, Music, Moon, Smartphone, Bot,
  ArrowRight, CheckCircle
} from 'lucide-vue-next'
import SpotifyIcon from '@/components/SpotifyIcon.vue'

// Navbar scroll behaviour
const scrolled = ref(false)
const onScroll = () => { scrolled.value = window.scrollY > 20 }
onMounted(() => window.addEventListener('scroll', onScroll, { passive: true }))
onUnmounted(() => window.removeEventListener('scroll', onScroll))

// Mock album cover colors for the hero visual
const albumColors = [
  'linear-gradient(135deg,#1DB954,#0f7a39)',
  'linear-gradient(135deg,#7c3aed,#4f25a0)',
  'linear-gradient(135deg,#f59e0b,#b45309)',
  'linear-gradient(135deg,#06b6d4,#0369a1)',
  'linear-gradient(135deg,#ec4899,#9d174d)',
  'linear-gradient(135deg,#64748b,#334155)',
  'linear-gradient(135deg,#10b981,#065f46)',
  'linear-gradient(135deg,#f97316,#9a3412)',
  'linear-gradient(135deg,#8b5cf6,#5b21b6)',
  'linear-gradient(135deg,#14b8a6,#0f766e)',
  'linear-gradient(135deg,#e879f9,#86198f)',
  'linear-gradient(135deg,#a3e635,#3f6212)',
]

const demoTags = [
  { label: 'Chill', bg: '#1DB95415', border: '#1DB95440', text: '#1DB954' },
  { label: 'Late Night', bg: '#7c3aed15', border: '#7c3aed40', text: '#7c3aed' },
  { label: 'Workout', bg: '#f59e0b15', border: '#f59e0b40', text: '#b45309' },
  { label: 'Focus', bg: '#06b6d415', border: '#06b6d440', text: '#0369a1' },
]

const features = [
  {
    icon: Folder,
    title: 'Smart Folders',
    description: 'Organize albums into nested folders with drag & drop. Build any hierarchy that fits your mind.',
    iconBg: 'bg-[#1DB954]/10',
    iconColor: 'text-[#1DB954]',
  },
  {
    icon: Tag,
    title: 'Custom Tags',
    description: 'Label albums with colorful tags for quick filtering. Create your own classification system.',
    iconBg: 'bg-violet-500/10',
    iconColor: 'text-violet-500',
  },
  {
    icon: Music,
    title: 'Genre & Artist Browser',
    description: 'Explore your collection by genre or artist. Discover patterns in your listening history.',
    iconBg: 'bg-sky-500/10',
    iconColor: 'text-sky-500',
  },
  {
    icon: Moon,
    title: 'Dark Mode',
    description: 'Beautiful dark theme that\'s easy on the eyes. Switch seamlessly between light and dark.',
    iconBg: 'bg-indigo-500/10',
    iconColor: 'text-indigo-500',
  },
  {
    icon: Smartphone,
    title: 'Mobile Ready',
    description: 'Responsive design that works on any device. Full feature parity from phone to desktop.',
    iconBg: 'bg-pink-500/10',
    iconColor: 'text-pink-500',
  },
  {
    icon: Bot,
    title: 'AI Integration',
    description: 'Control your library via AI assistant with MCP. Organize albums with natural language.',
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-500',
  },
]

const steps = [
  {
    icon: Music,
    title: 'Connect your Spotify account',
    description: 'Sign in securely with Spotify OAuth. Your existing library is imported automatically.',
  },
  {
    icon: Folder,
    title: 'Import and organize your albums',
    description: 'Create folders, add tags, and arrange albums exactly the way you think about music.',
  },
  {
    icon: CheckCircle,
    title: 'Enjoy your curated collection',
    description: 'Browse, filter, and rediscover your music with lightning-fast search and smart views.',
  },
]
</script>
